import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import {
  ChartBarIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const WASTE_CATEGORIES = {
  broken: { label: 'שבור/נזק', icon: '💥', color: '#ef4444' },
  lost: { label: 'אבד', icon: '❓', color: '#f59e0b' },
  expired: { label: 'פג תוקף', icon: '⏰', color: '#f97316' },
  damaged: { label: 'פגום', icon: '⚠️', color: '#eab308' },
  defective: { label: 'פגם יצרני', icon: '🔧', color: '#a855f7' },
  other: { label: 'אחר', icon: '📝', color: '#6b7280' }
};

const WasteAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchStats();
  }, [startDate, endDate]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await axios.get('/api/waste-reports/stats', { params });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">אנליטיקת בלאי</h1>
        <p className="text-gray-600 mt-1">ניתוח נתוני הפסדים ובלאי</p>
      </div>

      {/* Date Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex items-center gap-4">
          <CalendarIcon className="h-5 w-5 text-gray-400" />
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
          <span className="text-gray-500">עד</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          />
          {(startDate || endDate) && (
            <button
              onClick={() => { setStartDate(''); setEndDate(''); }}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              נקה
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-4">
            <div className="bg-red-100 p-4 rounded-lg">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">{stats?.total_waste_items || 0}</p>
              <p className="text-sm text-gray-600 mt-1">סה"כ פריטי בלאי</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-4">
            <div className="bg-orange-100 p-4 rounded-lg">
              <CurrencyDollarIcon className="h-8 w-8 text-orange-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">
                ₪{stats?.total_financial_loss?.toLocaleString() || 0}
              </p>
              <p className="text-sm text-gray-600 mt-1">הפסד כספי</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center gap-4">
            <div className="bg-purple-100 p-4 rounded-lg">
              <ChartBarIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">
                {stats?.waste_by_category?.length || 0}
              </p>
              <p className="text-sm text-gray-600 mt-1">קטגוריות בלאי</p>
            </div>
          </div>
        </div>
      </div>

      {/* Waste by Category */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">בלאי לפי קטגוריה</h2>
        <div className="space-y-3">
          {stats?.waste_by_category?.map(cat => {
            const categoryInfo = WASTE_CATEGORIES[cat.category] || WASTE_CATEGORIES.other;
            const percentage = stats.total_waste_items > 0 
              ? ((cat.total_quantity / stats.total_waste_items) * 100).toFixed(1) 
              : 0;

            return (
              <div key={cat.category} className="flex items-center gap-4">
                <div className="flex items-center gap-2 w-48">
                  <span className="text-2xl">{categoryInfo.icon}</span>
                  <span className="font-semibold text-gray-700">{categoryInfo.label}</span>
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-6 overflow-hidden">
                      <div
                        className="h-full flex items-center justify-center text-white text-xs font-bold transition-all duration-500"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: categoryInfo.color,
                          minWidth: percentage > 0 ? '30px' : '0'
                        }}
                      >
                        {percentage > 5 && `${percentage}%`}
                      </div>
                    </div>
                    <div className="w-24 text-right">
                      <span className="font-bold text-gray-900">{cat.total_quantity}</span>
                      <span className="text-sm text-gray-600"> פריטים</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top Waste Products */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">מוצרים עם הכי הרבה בלאי</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase">#</th>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase">מק"ט</th>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase">מוצר</th>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase">כמות בלאי</th>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase">דוחות</th>
                <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 uppercase">הפסד משוער</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stats?.top_waste_products?.map((item, index) => (
                <tr key={item.product.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {item.product.sku}
                    </code>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-semibold text-gray-900">{item.product.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-lg font-bold text-red-600">{item.total_quantity}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {item.report_count} דוחות
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-bold text-orange-600">
                      ₪{item.estimated_loss?.toLocaleString()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WasteAnalytics;

